import React from 'react';
import styled from 'styled-components';
import { FaWhatsapp, FaEnvelope, FaLinkedin, FaGithub, FaTwitter } from 'react-icons/fa';

const ContactContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 6rem 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: var(--secondary-color);
    margin: 0.5rem auto 0;
  }
`;

const ContactSubtitle = styled.p`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-size: 1.05rem;
  color: var(--dark-gray);
`;

const ContactContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: stretch;
  }
`;

/* ── WhatsApp card ── */

const WhatsAppCard = styled.div`
  flex: 1;
  background: var(--white);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.25rem;
`;

const WAIconWrap = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  background-color: #f0fdf4;
  border: 1px solid #bbf7d0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #16a34a;
`;

const WAHeading = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--primary-color);
  margin: 0;
`;

const WABody = styled.p`
  font-size: 0.975rem;
  color: var(--dark-gray);
  line-height: 1.65;
  margin: 0;
`;

const WAButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  background-color: #16a34a;
  color: #fff;
  padding: 0.75rem 1.35rem;
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: var(--transition);
  margin-top: auto;

  &:hover {
    background-color: #15803d;
    color: #fff;
    transform: translateY(-2px);
  }
`;

const WANote = styled.p`
  font-size: 0.8rem;
  color: var(--dark-gray);
  margin: 0;
`;

/* ── Contact info card ── */

const ContactInfoContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  background: var(--white);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  padding: 2rem;
`;

const ContactInfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const ContactIcon = styled.div`
  font-size: 1.3rem;
  color: var(--secondary-color);
  min-width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--light-gray);
  border-radius: 50%;
`;

const ContactText = styled.div`
  h4 {
    font-size: 0.95rem;
    font-weight: 600;
    margin-bottom: 0.2rem;
  }
  
  p, a {
    font-size: 0.9rem;
    color: var(--dark-gray);
    text-decoration: none;
    word-break: break-all;
  }
  
  a:hover {
    color: var(--secondary-color);
    text-decoration: underline;
  }
`;

const Divider = styled.div`
  height: 1px;
  background-color: var(--border-color);
`;

const ResumeButton = styled.a`
  display: inline-block;
  background-color: var(--secondary-color);
  color: white;
  padding: 0.55rem 1rem;
  border-radius: 999px;
  margin-top: 0.75rem;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: var(--transition);

  &:hover {
    background-color: var(--primary-color);
    color: white;
  }
`;

const SocialLinksContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: auto;
`;

const SocialLink = styled.a`
  font-size: 1.4rem;
  color: var(--primary-color);
  transition: var(--transition);
  
  &:hover {
    color: var(--secondary-color);
    transform: translateY(-3px);
  }
`;

const WHATSAPP_NUMBER = '971505138370'; // UAE: +971 505138370
const WHATSAPP_MESSAGE = encodeURIComponent("Hi Sultan, I came across your portfolio and would love to connect!");
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`;

const Contact = () => {
  return (
    <ContactContainer>
      <SectionTitle>Get In Touch</SectionTitle>

      <ContactSubtitle>
        I'm currently open to new opportunities and collaborations. Feel free to reach out if you have a question or just want to connect.
      </ContactSubtitle>

      <ContactContent>

        {/* WhatsApp CTA */}
        <WhatsAppCard>
          <WAIconWrap>
            <FaWhatsapp />
          </WAIconWrap>

          <WAHeading>Chat on WhatsApp</WAHeading>

          <WABody>
            Skip the inbox. Send me a message directly on WhatsApp and I'll get back to you within a few hours. Whether it's a job opportunity, project idea, or just a hello — I'm happy to chat.
          </WABody>

          <WAButton href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
            <FaWhatsapp />
            Open WhatsApp Chat
          </WAButton>

          <WANote>Opens WhatsApp Web or the app on mobile.</WANote>
        </WhatsAppCard>

        {/* Contact info */}
        <ContactInfoContainer>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Other Ways to Reach Me</h3>

          <ContactInfoItem>
            <ContactIcon><FaEnvelope /></ContactIcon>
            <ContactText>
              <h4>Email</h4>
              <a href="mailto:sultanmalik1800@gmail.com">sultanmalik1800@gmail.com</a>
            </ContactText>
          </ContactInfoItem>

          <ContactInfoItem>
            <ContactIcon><FaLinkedin /></ContactIcon>
            <ContactText>
              <h4>LinkedIn</h4>
              <a href="https://www.linkedin.com/in/malik-sultan-ahmed/" target="_blank" rel="noopener noreferrer">
                linkedin.com/in/malik-sultan-ahmed
              </a>
            </ContactText>
          </ContactInfoItem>

          <ContactInfoItem>
            <ContactIcon><FaGithub /></ContactIcon>
            <ContactText>
              <h4>GitHub</h4>
              <a href="https://github.com/sultanmalik04" target="_blank" rel="noopener noreferrer">
                github.com/sultanmalik04
              </a>
            </ContactText>
          </ContactInfoItem>

          <Divider />

          <div>
            <h4 style={{ fontSize: '0.95rem', fontWeight: 600 }}>Resume</h4>
            <p style={{ fontSize: '0.9rem', color: 'var(--dark-gray)', marginTop: '0.25rem' }}>
              View or download my resume to learn more about my experience.
            </p>
            <ResumeButton href="/resume.pdf" target="_blank" rel="noopener noreferrer">
              Download Resume
            </ResumeButton>
          </div>

          <SocialLinksContainer>
            <SocialLink href="https://github.com/sultanmalik04" target="_blank" rel="noopener noreferrer">
              <FaGithub />
            </SocialLink>
            <SocialLink href="https://www.linkedin.com/in/malik-sultan-ahmed/" target="_blank" rel="noopener noreferrer">
              <FaLinkedin />
            </SocialLink>
            <SocialLink href="https://x.com/_sultanmalik" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialLink>
          </SocialLinksContainer>
        </ContactInfoContainer>

      </ContactContent>
    </ContactContainer>
  );
};

export default Contact;
